import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { UseOrganizationMember } from 'features/users/hooks/useOrganizationMember';
import {
  OrganizationMember,
  OrganizationRole,
  UserPlanType,
} from 'features/users/users.types';
import { useConfirm } from 'components/ConfirmDialog';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { getShallowDiff } from 'shared/utils';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useAuthState } from 'state/useAuthState';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';

type OrganizationMemberProps = {
  member?: OrganizationMember;
  saveMember: UseOrganizationMember['saveMember'];
  updateMember: UseOrganizationMember['updateMember'];
  deleteMember: UseOrganizationMember['deleteMember'];
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: OrganizationRole;
  planType: UserPlanType | null;
};

type ServerErrorMessages = Record<
  string,
  { field: keyof FormValues; message: string }
>;

const serverErrorMessages: ServerErrorMessages = {
  'user-already-exists': {
    field: 'email',
    message: 'organizationMember.inputs.email.alreadyRegistered',
  },
  'user-already-in-organization': {
    field: 'email',
    message: 'organizationMember.inputs.email.alreadyInOrganization',
  },
  'user-already-in-another-organization': {
    field: 'email',
    message: 'organizationMember.inputs.email.alreadyInAnotherOrganization',
  },
};

export const EditOrganizationMemberForm = (
  props: OrganizationMemberProps,
): JSX.Element => {
  const { member, saveMember, updateMember, deleteMember } = props;
  const { t } = useTranslation();
  const { user } = useAuthState();
  const confirm = useConfirm();

  const { control, formState, handleSubmit, setError } = useForm<FormValues>({
    resolver: yupResolver(
      Yup.object().shape({
        firstName: Yup.string().required(
          t('organizationMember.inputs.firstName.required'),
        ),
        lastName: Yup.string().required(
          t('organizationMember.inputs.lastName.required'),
        ),
        email: Yup.string().when('$create', {
          is: true,
          then: Yup.string()
            .email(t('organizationMember.inputs.email.invalid'))
            .required(t('organizationMember.inputs.email.required')),
          otherwise: Yup.string().email().nullable(),
        }),
        role: Yup.string().required(
          t('organizationMember.inputs.role.required'),
        ),
        planType: Yup.string().nullable(),
      }),
    ),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: 'member',
      planType: null,
    },
    values: {
      firstName: member?.firstName || '',
      lastName: member?.lastName || '',
      email: member?.email || '',
      role: member?.role || 'member',
      planType: member?.planType || null,
    },
    mode: 'onBlur',
    context: { create: !member },
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const handleSubmitMember: SubmitHandler<FormValues> = async (values) => {
    if (!member) {
      try {
        await saveMember.mutateAsync({
          ...values,
          planType: values.planType,
        });
        unblockedNavigate('/organization');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const errorKey: keyof ServerErrorMessages | undefined =
          error.response?.data?.[0];
        if (errorKey && serverErrorMessages[errorKey]) {
          const { field, message } = serverErrorMessages[errorKey];
          setError(field, { message: t(message) });
          return;
        }
        logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      }
      return;
    }

    const { diff, isEmpty } = getShallowDiff<FormValues>(member, values);

    try {
      if (!isEmpty) {
        await updateMember.mutateAsync(diff);
      }
      unblockedNavigate('/organization');
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const handleDeleteMember = async (): Promise<void> => {
    try {
      const resp = await confirm(t('organizationMember.deleteConfirmation'));
      if (!resp) {
        return;
      }
      await deleteMember.mutateAsync();
      enqueueSnackbar(t('organizationMember.deleteSuccess'), {
        variant: 'success',
      });
      unblockedNavigate('/organization');
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  // Check if the user is self or an owner this will be enforced in the backend as well
  const canDeleteMember =
    Boolean(member) &&
    member?.role !== 'owner' &&
    member?.email !== user?.email;

  // Admins/owners cannot change their own role, and no one can change the role of an owner
  const cantUpdateRole =
    member?.email === user?.email || member?.role === 'owner';

  return (
    <Stack
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleSubmitMember)}
    >
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => {
              const { ref, ...muiFieldProps } = field;
              return (
                <TextField
                  id="field-control-firstName"
                  label={t('organizationMember.inputs.firstName.label')}
                  fullWidth
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={formState.isSubmitting}
                />
              );
            }}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => {
              const { ref, ...muiFieldProps } = field;
              return (
                <TextField
                  id="field-control-lastName"
                  label={t('organizationMember.inputs.lastName.label')}
                  fullWidth
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={formState.isSubmitting}
                />
              );
            }}
          />
        </Grid>
      </Grid>

      <Grid>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-email"
                label={t('organizationMember.inputs.email.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={Boolean(member) || formState.isSubmitting}
              />
            );
          }}
        />
      </Grid>

      <Grid>
        <Controller
          control={control}
          name="role"
          render={({ field, fieldState }) => {
            const { ref, value, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-role"
                select
                label={t('organizationMember.inputs.role.label')}
                fullWidth
                value={value ?? ''}
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting || cantUpdateRole}
              >
                <MenuItem value="member">
                  {t('organizationMember.inputs.role.values.member')}
                </MenuItem>
                <MenuItem value="admin">
                  {t('organizationMember.inputs.role.values.admin')}
                </MenuItem>
                {member?.role === 'owner' && (
                  <MenuItem value="owner">
                    {t('organizationMember.inputs.role.values.owner')}
                  </MenuItem>
                )}
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid>
        <Controller
          control={control}
          name="planType"
          render={({ field, fieldState }) => {
            const { ref, value, onChange, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-planType"
                select
                label={t('organizationMember.inputs.planType.label')}
                fullWidth
                value={value ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(val === '' ? null : (val as UserPlanType));
                }}
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting}
                slotProps={{
                  select: { displayEmpty: true },
                  inputLabel: { shrink: true },
                }}
              >
                <MenuItem value="">
                  {t('organizationMember.inputs.planType.values.null')}
                </MenuItem>
                <MenuItem value="regular">
                  {t('organizationMember.inputs.planType.values.regular')}
                </MenuItem>
                <MenuItem value="pro">
                  {t('organizationMember.inputs.planType.values.pro')}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid>
        <Alert severity="info" icon={false}>
          <Typography variant="body2">
            <Trans i18nKey="organizationMember.info">
              Need to invite a large group of users? Email us at
              <Link href="mailto:support@watchduty.org" color="inherit">
                support@watchduty.org
              </Link>
              and we&apos;ll help import them for you.
            </Trans>
          </Typography>
        </Alert>
      </Grid>

      <Stack spacing={2} sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        {canDeleteMember && (
          <Button
            fullWidth
            size="large"
            onClick={handleDeleteMember}
            loading={deleteMember.isPending}
            loadingPosition="end"
            color="error"
          >
            {t('organizationMember.buttons.delete')}
          </Button>
        )}

        <Button
          fullWidth
          size="large"
          type="submit"
          loading={formState.isSubmitting}
          loadingPosition="end"
        >
          {t('organizationMember.buttons.save')}
        </Button>
      </Stack>
    </Stack>
  );
};
